import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * Tipos de input que NO se transforman a mayúsculas. Debe mantenerse en
 * sincronía con la regla CSS de `src/index.css` que aplica
 * `text-transform: uppercase` visualmente para que el valor mostrado y el
 * valor almacenado coincidan.
 */
const NO_UPPERCASE_TYPES = new Set([
  "email",
  "password",
  "number",
  "checkbox",
  "radio",
  "file",
  "date",
  "time",
  "datetime-local",
  "color",
  "range",
]);

const Input = React.forwardRef<HTMLInputElement, React.ComponentProps<"input">>(
  ({ className, type, onChange, ...props }, ref) => {
    const shouldUppercase =
      !NO_UPPERCASE_TYPES.has(type ?? "text") &&
      (props as { "data-no-uppercase"?: unknown })["data-no-uppercase"] === undefined;

    const handleChange = React.useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        if (shouldUppercase && typeof e.target.value === "string") {
          const upper = e.target.value.toUpperCase();
          if (upper !== e.target.value) {
            e.target.value = upper;
          }
        }
        onChange?.(e);
      },
      [onChange, shouldUppercase],
    );

    return (
      <input
        type={type}
        className={cn(
          "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          className,
        )}
        ref={ref}
        onChange={handleChange}
        {...props}
      />
    );
  },
);
Input.displayName = "Input";

export { Input };
